import {Car} from "../component/Car";
import {DIRECTION, JW_TYPE} from "../component/enums/RoadKillEnums";
import {gsap, TimelineLite} from "gsap";
import {RoadKill} from "../RoadKill";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {RKHelper} from "../utils/RKHelper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {ICarAnimalData, IMovingCarData} from "../gameflow/RESPIN/RoadKillRespinResultPresentation";
import {RoadKillSound} from "../sound/RoadKillSound";

export class MovingCar extends PIXI.Container implements StageLayer {
    private _carHilly: Car;
    private _carBilly: Car;
    private _carTease: Car;
    private _isCarTeasePlaying: boolean = false;
    private _tl: TimelineLite;

    constructor() {
        super();
        this.createCar();
        this.name = "MovingCar";
    }

    public reset(): void {
        this._isCarTeasePlaying = false;
        if (this._carTease.visible) {
            gsap.to(this._carTease, {
                alpha: 0, duration: 0.1, onComplete: () => {
                    this._carTease.visible = false;
                }
            });
        }
        this._carBilly.reset();
        this._carHilly.reset();
    }

    public setCarDirectionAndPos(fromDirection: DIRECTION, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this._carTease.alpha = 0;
            this._carTease.visible = true;
            this._carTease.scale.set(DIRECTION.LEFT === fromDirection ? -1 : 1);
            this._carTease.position.set(DIRECTION.LEFT === fromDirection ? NolimitApplication.screenBounds.left : NolimitApplication.screenBounds.right, RoadKillUtils.getSymAbsolutePos(0, rowId).y - 100);
        });
        return tl;
    }

    public playCarTease(): TimelineLite {
        this._isCarTeasePlaying = true;
        this._tl = gsap.timeline();
        this._tl.add(() => {
            gsap.to(this._carTease, {alpha: 1, duration: 0.01});
        }, 0);
        this._tl.add(() => {
            this._carTease.state.setAnimation(0, "lightFocus", true);
        }, 0);
        return this._tl;
    }

    public stopCarTease(firstRowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._carTease.visible && this._carTease.alpha > 0 && this._isCarTeasePlaying) {
            this._isCarTeasePlaying = false;
            const yPos: number = RoadKillUtils.getSymAbsolutePos(0, firstRowId).y - ((firstRowId === 1) ? 50 : 100);
            tl.to(this._carTease, {duration: 0.1, y: yPos});
            tl.to(this._carTease, {duration: 0.1});
            tl.add(() => {
                gsap.to(this._carTease, {alpha: 0, duration: 0.1})
            });
            tl.add(() => {
                this._carTease.state.setEmptyAnimation(0, 0);
                this._carTease.visible = false;
            });
        }
        return tl;
    }

    public moveCar(movingCarData: IMovingCarData, idx: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const firstRowId: number = movingCarData.rowId;
        if (DIRECTION.RIGHT === movingCarData.direction && this._carTease.visible && this._isCarTeasePlaying) {
            tl.add(this.stopCarTease(firstRowId));
        }
        if (DIRECTION.LEFT === movingCarData.direction) {
            tl.add(() => {
                RoadKill.lifeView.onDanger();
            });
        }
        movingCarData.destReelIds.forEach((destReelId: number, index: number) => {
            const isLast: boolean = (index === movingCarData.destReelIds.length - 1);
            tl.add(() => {
                if (movingCarData.name === "Red" && movingCarData.hitByAnimal && movingCarData.hitByAnimal[0] !== "Bear") {
                    if (movingCarData.destReelIds[0] !== -1 && destReelId === movingCarData.destReelIds[1]) {
                        RoadKill.soundPlayer.playTrack(RoadKillSound.CAR_CRASHING_DRIVE_AWAY)
                    } else {
                        RoadKill.soundPlayer.playTrack(RoadKillSound.CAR_MOVE)
                    }
                } else {
                    RoadKill.soundPlayer.playTrack(RoadKillSound.CAR_MOVE)
                }
            });
            tl.add(this.play(movingCarData.direction, movingCarData.rowId, movingCarData.name, destReelId, index !== 0, isLast));
            if (destReelId !== -1 && movingCarData.animalData && movingCarData.hitByAnimal) {
                const carData: ICarAnimalData = {
                    name: movingCarData.name,
                    rowId: movingCarData.rowId,
                    sourceReelId: movingCarData.sourceReelId,
                    destReelId: destReelId,
                    animalData: movingCarData.animalData[index],
                    direction: movingCarData.direction,
                    hpLeft: movingCarData.hpLeft[index],
                }
                tl.add(() => {
                    RoadKill.soundPlayer.playTrack(RoadKillSound.CAR_CRASH);
                    RoadKill.soundPlayer.stopTrack(RoadKillSound.CAR_MOVE);
                    RoadKill.soundPlayer.stopTrack(RoadKillSound.CAR_CRASHING_DRIVE_AWAY);
                }, ">-0.2");
                tl.addLabel("carMoveComplete");
                if (carData.name === "Red") {
                    tl.add(RoadKill.backgroundElements.disableBillyTyres(carData.hpLeft))
                } else {
                    tl.add(RoadKill.backgroundElements.disableHilly())
                }
                tl.add(() => {
                    RoadKill.soundPlayer.playTrack(RoadKillSound.HILLI_BILLY_REMOVE_TYRE);
                }, "<")
                tl.add(RoadKill.jumpingWildView.onHit(carData), "carMoveComplete");
                const currentCar: Car = movingCarData.name === "Red" ? this._carBilly : this._carHilly;
                if (carData.animalData) {
                    const type: JW_TYPE = RoadKillUtils.getJWType(carData.animalData.symName);
                    if (carData.hpLeft === 0 || RoadKill.model.gameData.doMassiveWildThisSpin) {
                        if (type === JW_TYPE.TRAP) {
                            tl.add(RKHelper.playSpineWithTimeline(currentCar, 0, "trapped"), "carMoveComplete");
                        } else {
                            tl.add(RKHelper.playSpineWithTimeline(currentCar, 0, "destroyed"), "carMoveComplete");
                        }
                    } else if (movingCarData.name === "Red") {
                        tl.add(RKHelper.playSpineWithTimeline(currentCar, 0, "hit"), "carMoveComplete");
                    }
                    if (isLast) {
                        tl.add(gsap.to(currentCar, {alpha: 0, duration: 0.1}));
                    }
                }
            }
        });
        if (RoadKill.model.isMassiveWin && idx === 1) {
            tl.add([RoadKill.massiveWinView.showFullCover(), () => {
                RoadKill.soundPlayer.playTrack(RoadKillSound.ROADBLOCK);
            }]);
        }
        return tl;
    }

    public play(fromDirection: DIRECTION | string, rowId: number, carName: string, toDest: number, continueFromLast: boolean, isLast: boolean): TimelineLite {
        const currentCar: Car = carName === "Red" ? this._carBilly : this._carHilly;
        currentCar.alpha = 0;
        currentCar.visible = true;
        const tl: TimelineLite = new TimelineLite();
        if (!continueFromLast) {
            tl.add(() => {
                gsap.to(currentCar, {alpha: 1, duration: 0.01});
                currentCar.scale.x = (DIRECTION.LEFT === fromDirection ? -1 : 1);
                currentCar.position.set(DIRECTION.LEFT === fromDirection ? 0 : NolimitApplication.screenBounds.width, RoadKillUtils.getSymAbsolutePos(0, rowId).y);
            });
        }
        let duration: number = 1;
        let posX: number = DIRECTION.LEFT === fromDirection ? (NolimitApplication.screenBounds.width + 500) : -900;
        if (toDest > -1) {
            posX = RoadKillUtils.getSymAbsolutePos(toDest, rowId).x;
            duration = 0.3;
        }

        tl.add([
            gsap.to(currentCar, {duration: duration, x: posX}),
            RKHelper.playSpineWithTimeline(currentCar, 0, "fromRight").timeScale(1.5 / duration), //1.3s
        ]);
        if (DIRECTION.LEFT === fromDirection) {
            tl.add(() => {
                RoadKill.lifeView.onCarMoveDone(toDest === -1);
            }, toDest > -1 ? 0.2 : 0.4);
        }
        if (toDest > -1) {
            tl.add(currentCar.onHit());
            tl.add(gsap.to(currentCar, {duration: 0.1}));
        } else {
            if (isLast) {
                tl.add(gsap.to(currentCar, {alpha: 0, duration: 0.1}));
            }
        }
        return tl;
    }

    private setInitialStopPos(currentCar: Car): void {
        currentCar.pivot.x = -200;
        currentCar.pivot.y = -30;
    }

    private createCar(): void {
        this._carHilly = new Car(0, RoadKill.assetLoader.getSpineAsset("carHilly"));
        this._carBilly = new Car(1, RoadKill.assetLoader.getSpineAsset("carBilly"));
        this._carTease = new Car(1, RoadKill.assetLoader.getSpineAsset("carBilly"));
        this.setInitialStopPos(this._carHilly);
        this.setInitialStopPos(this._carBilly);
        this.addChild(this._carHilly, this._carBilly, this._carTease);
        this._carHilly.visible = this._carBilly.visible = this._carTease.visible = false;
    }


}